import React, { useEffect, useState } from "react";
import api from "../services/api";


// Same vehicle keys the backend uses; labels match the driver app
const VEHICLE_TYPES = [
  { key: "bike", label: "Bike" },
  { key: "ape", label: "3 Wheeler" },
  { key: "pickup", label: "Pickup" },
  { key: "truck", label: "Truck" },
];



const PlatformSettings = () => {
  const [cancellationFeePercent, setCancellationFeePercent] = useState(0);
  const [commissionPercent, setCommissionPercent] = useState(10);



  // Booking radius filter
  const [radiusEnabled, setRadiusEnabled] = useState(false);
  const [expandEveryMinutes, setExpandEveryMinutes] = useState(5);
  const [expandStepKm, setExpandStepKm] = useState(2);
  const [radiusVehicles, setRadiusVehicles] = useState({
    bike: { baseKm: 10, maxKm: 30 },
    ape: { baseKm: 10, maxKm: 30 },
    pickup: { baseKm: 10, maxKm: 30 },
    truck: { baseKm: 10, maxKm: 30 },
  });



  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");

  const fetchSettings = async () => {
    try {
      setLoading(true);
      const res = await api.get("/settings");
      if (res.data.success) {
        setCancellationFeePercent(res.data.settings.cancellationFeePercent);
        setCommissionPercent(res.data.settings.commissionPercent);



        const r = res.data.settings.bookingRadius;
        if (r) {
          setRadiusEnabled(r.enabled === true);
          setExpandEveryMinutes(r.expandEveryMinutes ?? 5);
          setExpandStepKm(r.expandStepKm ?? 2);
          setRadiusVehicles((prev) => {
            const next = { ...prev };
            VEHICLE_TYPES.forEach(({ key }) => {
              next[key] = {
                baseKm: r.vehicles?.[key]?.baseKm ?? prev[key].baseKm,
                maxKm: r.vehicles?.[key]?.maxKm ?? prev[key].maxKm,
              };
            });
            return next;
          });
        }


      }
    } catch (err) {
      setError(err.response?.data?.message || "Failed to load settings");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSettings();
  }, []);

  const handleSave = async () => {
    try {
      setSaving(true);
      setMessage("");
      setError("");


        // An empty box would be saved as 0 km and hide every booking, so stop here
      const hasEmptyRadius =
        expandEveryMinutes === "" ||
        expandStepKm === "" ||
        VEHICLE_TYPES.some(
          ({ key }) => radiusVehicles[key].baseKm === "" || radiusVehicles[key].maxKm === ""
        );
      if (hasEmptyRadius) {
        setError("Please fill in every radius field");
        return; // the "finally" below turns the Saving... state off
      }

      const res = await api.put("/settings", {
        cancellationFeePercent: Number(cancellationFeePercent),
        commissionPercent: Number(commissionPercent),



         bookingRadius: {
          enabled: radiusEnabled,
          expandEveryMinutes: Number(expandEveryMinutes),
          expandStepKm: Number(expandStepKm),
          vehicles: Object.fromEntries(
            VEHICLE_TYPES.map(({ key }) => [
              key,
              {
                baseKm: Number(radiusVehicles[key].baseKm),
                maxKm: Number(radiusVehicles[key].maxKm),
              },
            ])
          ),
        },



      });

      if (res.data.success) {
        setMessage("Settings updated successfully");
      }
    } catch (err) {
      setError(err.response?.data?.message || "Failed to update settings");
    } finally {
      setSaving(false);
    }
  };

   const updateRadius = (key, field, value) =>
    setRadiusVehicles((prev) => ({ ...prev, [key]: { ...prev[key], [field]: value } }));

  if (loading) {
    return <div style={styles.loading}>Loading Platform Settings...</div>;
  }

  return (
    <div style={styles.page}>
      <h2 style={styles.title}>Platform Settings</h2>
      <p style={styles.subtitle}>Control cancellation fees and driver commission across the whole platform</p>

      <div style={styles.card}>
        <label style={styles.label}>Cancellation Fee (%)</label>
        <p style={styles.helpText}>
          Percentage deducted from the refund when a booking is cancelled after payment. Applies to both customer self-cancel and admin manual cancel.
        </p>
        <input
          type="number"
          min="0"
          max="100"
          style={styles.input}
          value={cancellationFeePercent}
          onChange={(e) => setCancellationFeePercent(e.target.value)}
        />

        <label style={{ ...styles.label, marginTop: "24px" }}>Driver Commission (%)</label>
        <p style={styles.helpText}>
          Percentage the platform keeps from each completed trip's fare. The remainder is credited to the driver's wallet.
        </p>
        <input
          type="number"
          min="0"
          max="100"
          style={styles.input}
          value={commissionPercent}
          onChange={(e) => setCommissionPercent(e.target.value)}
        />

                <div style={styles.divider} />

        <label style={styles.label}>Nearby Bookings (Radius Filter)</label>
        <p style={styles.helpText}>
          When ON, a new booking is shown only to online drivers within this distance of its pickup point.
          If nobody takes it, the distance grows over time until it reaches the maximum.
          When OFF, every online driver with the matching vehicle type sees every booking.
        </p>

        <label style={styles.checkboxRow}>
          <input
            type="checkbox"
            checked={radiusEnabled}
            onChange={(e) => setRadiusEnabled(e.target.checked)}
          />
          <span style={{ marginLeft: "8px", fontSize: "14px", fontWeight: "600", color: "#1e293b" }}>
            Enable radius filter
          </span>
        </label>

        <div style={styles.radiusHeader}>
          <span style={{ flex: 1.2 }}>Vehicle</span>
          <span style={{ flex: 1, marginLeft: "8px" }}>Start (km)</span>
          <span style={{ flex: 1, marginLeft: "8px" }}>Max (km)</span>
        </div>

        {VEHICLE_TYPES.map(({ key, label }) => (
          <div key={key} style={styles.radiusRow}>
            <span style={{ flex: 1.2, fontSize: "14px", fontWeight: "600", color: "#1e293b" }}>{label}</span>
            <input
              type="number"
              min="0"
              style={{ ...styles.input, ...styles.smallInput }}
              value={radiusVehicles[key].baseKm}
              onChange={(e) => updateRadius(key, "baseKm", e.target.value)}
            />
            <input
              type="number"
              min="0"
              style={{ ...styles.input, ...styles.smallInput }}
              value={radiusVehicles[key].maxKm}
              onChange={(e) => updateRadius(key, "maxKm", e.target.value)}
            />
          </div>
        ))}

        <label style={{ ...styles.label, marginTop: "16px" }}>Grow the radius every (minutes)</label>
        <p style={styles.helpText}>Use 0 if the radius should never grow.</p>
        <input
          type="number"
          min="0"
          style={styles.input}
          value={expandEveryMinutes}
          onChange={(e) => setExpandEveryMinutes(e.target.value)}
        />

        <label style={{ ...styles.label, marginTop: "16px" }}>Grow by (km each time)</label>
        <input
          type="number"
          min="0"
          style={styles.input}
          value={expandStepKm}
          onChange={(e) => setExpandStepKm(e.target.value)}
        />

        {message && <p style={styles.successMsg}>{message}</p>}

        {error && <p style={styles.errorMsg}>{error}</p>}

        <button style={styles.saveBtn} onClick={handleSave} disabled={saving}>
          {saving ? "Saving..." : "Save Settings"}
        </button>
      </div>
    </div>
  );
};

const styles = {
  page: { padding: "30px", background: "#f8fafc", minHeight: "100vh", fontFamily: "'Inter', sans-serif" },
  title: { margin: 0, fontSize: "28px", color: "#1e293b", fontWeight: "700" },
  subtitle: { margin: "5px 0 24px 0", color: "#64748b", fontSize: "14px" },
  card: { background: "#fff", borderRadius: "16px", padding: "28px", maxWidth: "480px", boxShadow: "0 4px 6px -1px rgba(0,0,0,0.1)" },
  label: { display: "block", fontSize: "13px", fontWeight: "700", color: "#1e293b", marginBottom: "6px" },
  helpText: { fontSize: "12px", color: "#94a3b8", marginBottom: "10px", lineHeight: "1.5" },
  input: { width: "100%", padding: "12px 14px", borderRadius: "8px", border: "1px solid #e2e8f0", fontSize: "16px", boxSizing: "border-box" },
  saveBtn: { marginTop: "24px", padding: "12px 24px", borderRadius: "8px", border: "none", background: "#2563eb", color: "#fff", fontWeight: "600", cursor: "pointer" },
  successMsg: { color: "#166534", fontSize: "13px", fontWeight: "600", marginTop: "16px" },
  errorMsg: { color: "#b91c1c", fontSize: "13px", fontWeight: "600", marginTop: "16px" },
  loading: { textAlign: "center", padding: "100px", fontSize: "20px" },
  divider: { borderTop: "1px solid #e2e8f0", margin: "28px 0 20px 0" },
  checkboxRow: { display: "flex", alignItems: "center", cursor: "pointer", marginBottom: "16px" },
  radiusHeader: { display: "flex", fontSize: "12px", fontWeight: "700", color: "#64748b", marginBottom: "6px" },
  radiusRow: { display: "flex", alignItems: "center", marginBottom: "8px" },
  smallInput: { flex: 1, width: "auto", minWidth: 0, marginLeft: "8px", padding: "10px 12px", fontSize: "14px" },
};

export default PlatformSettings;